import { put } from 'redux-saga/effects';
import clientPayment from 'api/graphql/clientPayment';
import clientPaymentSlice from 'store/modules/clientPayment';
import trainerPayment from 'api/graphql/trainerPayment';
import trainerPaymentSlice from 'store/modules/trainerPayment';
import { getUserFriendlyError } from 'common/utils/general';

/**
 * A higher-order function that creates a saga for handling client payment listing.
 *
 * @param {function} successAction - The action to be dispatched upon a successful API call.
 * @param {function} errorAction - The action to be dispatched if the API call fails.
 *
 * @returns {function} A generator function (saga) that makes an API call to list client payments.
 */
export const createClientPaymentList = (successAction, errorAction) =>
  function* list(action) {
    try {
      const { fields, filters = {}, pagination } = action.payload;
      const response = yield clientPayment.list({ fields, filters, pagination });

      const {
        data: {
          clientPayments: { data, paginatorInfo }
        }
      } = response;

      yield put(successAction(data));
      yield put(
        clientPaymentSlice.actions.updatePaginatorInfo({
          hasMorePages: paginatorInfo.hasMorePages,
          lastPage: paginatorInfo.lastPage,
          total: paginatorInfo.total
        })
      );
    } catch (ex) {
      const message = getUserFriendlyError(ex);
      yield put(errorAction(message));
    }
  };

/**
 * A higher-order function that creates a saga for handling trainer payment listing
 *
 * @param {function} successAction - The action to be dispatched upon a successful API call
 * @param {function} errorAction - The action to be dispatched if the API call fails
 *
 * @returns {function} A generator function (saga) that makes an API call to list trainer payments
 */
export const createTrainerPaymentList = (successAction, errorAction) =>
  function* list(action) {
    try {
      const { fields, filters = {}, pagination } = action.payload;
      const response = yield trainerPayment.list({ fields, filters, pagination });

      const {
        data: {
          trainerPayments: { data, paginatorInfo }
        }
      } = response;

      yield put(successAction(data));
      yield put(
        trainerPaymentSlice.actions.updatePaginatorInfo({
          hasMorePages: paginatorInfo.hasMorePages,
          lastPage: paginatorInfo.lastPage,
          total: paginatorInfo.total
        })
      );
    } catch (ex) {
      const message = getUserFriendlyError(ex);
      yield put(errorAction(message));
    }
  };
